import React, { useState } from 'react';
import ReactLoading from 'react-loading';
import LocalizedMessage from 'react-l10n';
import OrderList from './OrderList';
import { getTranslation } from '../lang/main';
import useManualReactTableState from './ManualReactTableState';
import {
    axiosApprovalOrder,
    axiosRejectOrder,
    axiosUpdateOrder,
} from '../actions/orderActions';
import {
    ALL_ORDER_STATUSES,
    ALL_SHIPPING_STATUSES,
} from '../selectors/orderSelector';
import { handleAxiosErrors, currencyDisplay, currencyObject } from '../_utils';
import {
    createModal,
    hideModal,
} from '../dom/components/confirm-removal-modal';
import InlineFieldGroup from './fields/InlineFieldGroup';
import FieldSelect from './fields/FieldSelect';
import { convertPaymentMethodToAReadableFormat } from '../selectors/converterSelector';

const statusDropdown = (statuses, ignoreStatus) => {
    return statuses
        .filter((item) => item != ignoreStatus)
        .map((item) => {
            return {
                label: item,
                value: item,
            };
        });
};

const orderStatusDropdown = statusDropdown(ALL_ORDER_STATUSES, '');
const shippingStatusDropdown = statusDropdown(ALL_SHIPPING_STATUSES, '');

function CustomerOrders(props) {
    let memberType = window.memberType.toLowerCase();
    const [
        recentCustomerOrdersWatcher,
        setRecentCustomerOrdersWatcher,
    ] = useState(0);
    const [
        awaitingApprovalCustomerOrdersWatcher,
        setAwaitingApprovalCustomerOrdersWatcher,
    ] = useState(0);

    const mrtState = useManualReactTableState({});

    // User Input
    const { isUserInputLoading, toggleUserInputLoading } = mrtState;
    const { userInput, clearUserInput, updateUserInput } = mrtState;

    // Request
    const handleApprovalClick = (e, identifier, approvalStatus) => {
        const loadingKey = 'ApprovalStatus';

        const payload = {
            ID: identifier,
            //Status: approvalStatus ? 'Awaiting Upstream Approval' : 'Declined',
        };

        toggleUserInputLoading(identifier, loadingKey, true);

        const approvalOrder = () => {
            return axiosApprovalOrder(payload)
                .then((response) => {
                    const { data } = response;

                    if (typeof data.data.approveOrder != 'undefined') {
                        setRecentCustomerOrdersWatcher(
                            recentCustomerOrdersWatcher + 1
                        );
                        setAwaitingApprovalCustomerOrdersWatcher(
                            awaitingApprovalCustomerOrdersWatcher + 1
                        );
                    }

                    toggleUserInputLoading(identifier, loadingKey, false);
                })
                .catch((error) => {
                    handleAxiosErrors(error);
                    toggleUserInputLoading(identifier, loadingKey, false);
                });
        };

        const rejectOrder = () => {
            return axiosRejectOrder(payload)
                .then((response) => {
                    const { data } = response;

                    if (typeof data.data.voidOrder != 'undefined') {
                        setRecentCustomerOrdersWatcher(
                            recentCustomerOrdersWatcher + 1
                        );
                        setAwaitingApprovalCustomerOrdersWatcher(
                            awaitingApprovalCustomerOrdersWatcher + 1
                        );
                    }

                    toggleUserInputLoading(identifier, loadingKey, false);
                })
                .catch((error) => {
                    handleAxiosErrors(error);
                    toggleUserInputLoading(identifier, loadingKey, false);
                });
        };

        // Declined
        if (!approvalStatus) {
            const modalName = 'confirm-reject-order-modal';
            const data = {
                heading: getTranslation(
                    'REJECT_CUSTOM_ORDER_MODAL.REJECT_ORDER_HEADING'
                ),
                noticeText: getTranslation(
                    'REJECT_CUSTOM_ORDER_MODAL.REJECT_ORDER_CONTENT'
                ),
                confirm: getTranslation(
                    'REJECT_CUSTOM_ORDER_MODAL.REJECT_ORDER_CONFIRM'
                ),
            };

            createModal(
                modalName,
                data,
                () => {
                    hideModal(modalName);
                    rejectOrder();
                },
                () => {
                    hideModal(modalName);
                    toggleUserInputLoading(identifier, loadingKey, false);
                }
            );
        } else {
            // Approval
            approvalOrder();
        }
    };

    const saveOrderStatus = (identifier, key, value) => {
        toggleUserInputLoading(identifier, key, true);

        let payload = {
            ID: identifier,
            [key]: value,
        };

        return axiosUpdateOrder(payload)
            .then((response) => {
                const { data } = response;

                if (typeof data.data.updateOrder != 'undefined') {
                    setRecentCustomerOrdersWatcher(
                        recentCustomerOrdersWatcher + 1
                    );

                    if (value == 'Awaiting Approval') {
                        setAwaitingApprovalCustomerOrdersWatcher(
                            awaitingApprovalCustomerOrdersWatcher + 1
                        );
                    }
                }

                toggleUserInputLoading(identifier, key, false);

                // Clear user input
                clearUserInput(identifier, key);
            })
            .catch((error) => {
                handleAxiosErrors(error);
                toggleUserInputLoading(identifier, key, false);
            });
    };

    // Functions
    const getAwaitingApprovalCustomerOrdersCallback = () => {
        return {
            updateListParams: (listParams) => {
                listParams['filters']['View'] = 'customer_orders';
                listParams['filters']['PendingOnly'] = true;

                return listParams;
            },
            updateViewOrderParamsCallback: (listParams) => {
                listParams['filters']['View'] = 'customer_orders';

                return listParams;
            },
            updateTableProps: (
                tableProps,
                { loadOrder, toggleViewOrderModal, toggleRefineResultsModal }
            ) => {
                tableProps.list.columns = [
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_NUMBER'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'ORDER_NO'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'RESELLER'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ResellerName',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'NAME'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { FirstName, Surname } = row.original;
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {`${FirstName} ${Surname}`}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'FirstName',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'PLACED'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.PlacedNice}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Placed',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_STATUS'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Status',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'TAGS'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'ContainItemTypes',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage
                                            id={'PAYMENT_METHOD'}
                                        />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {convertPaymentMethodToAReadableFormat(row.value)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'PaymentMethod',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'TOTAL'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { CurrencyDisplayCode, CurrencyFormat } = row.original;
                            const { locale } = currencyObject;

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {currencyDisplay(row.value, CurrencyDisplayCode, CurrencyFormat, locale)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'Total',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage
                                            id={'ORDER_APPROVAL'}
                                        />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const identifier = row.original.ID;

                            return (
                                <React.Fragment>
                                    {isUserInputLoading(
                                        identifier,
                                        'ApprovalStatus'
                                    ) ? (
                                        <div
                                            style={{
                                                position: 'relative',
                                                height: 35,
                                                width: '100%',
                                            }}
                                        >
                                            <ReactLoading
                                                type='spin'
                                                className='button__spinner'
                                                color='black'
                                                height={20}
                                                width={20}
                                            />
                                        </div>
                                    ) : (
                                        <span
                                            className='react-table__info'
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                type='button'
                                                className='button button--less-fat button--green'
                                                onClick={(e) =>
                                                    handleApprovalClick(
                                                        e,
                                                        identifier,
                                                        true
                                                    )
                                                }
                                            >
                                                <span className='icon icon-check-thick' />
                                            </button>
                                            <button
                                                type='button'
                                                className='button button--less-fat button--red'
                                                onClick={(e) =>
                                                    handleApprovalClick(
                                                        e,
                                                        identifier,
                                                        false
                                                    )
                                                }
                                            >
                                                <span className='icon icon-close-thick' />
                                            </button>
                                        </span>
                                    )}
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        width: '150',
                        accessor: 'ID',
                        show: memberType === 'wholesaler' ? false : true,
                    },
                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => {
                            return (
                                <React.Fragment>
                                    <button
                                        type='button'
                                        className='react-table__extra-data-toggle'
                                    >
                                        <span className='icon icon-chevron-thick' />
                                    </button>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ];

                return tableProps;
            },
        };
    };

    const getRecentCustomerOrdersCallback = () => {
        return {
            updateListParams: (listParams) => {
                listParams['filters']['View'] = 'customer_orders';
                // listParams['filters']['ApprovedOnly'] = true;

                // if(typeof(listParams['filters']['Status:not']) == 'undefined') {
                //     listParams['filters']['Status:not'] = [];
                // }
                //
                // listParams['filters']['Status:not'].push('Awaiting Approval');

                return listParams;
            },
            updateViewOrderParamsCallback: (listParams) => {
                listParams['filters']['View'] = 'customer_orders';

                return listParams;
            },
            updateTableProps: (
                tableProps,
                { loadOrder, toggleViewOrderModal, toggleRefineResultsModal }
            ) => {
                tableProps.list.columns = [
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_NUMBER'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'ORDER_NO'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'InternalItemID',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'SOLD_BY'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { BusinessName, FirstName, Surname } = row.value;

                            if(BusinessName) {
                                return (
                                    <React.Fragment>
                                        <span className='react-table__info'>
                                            {BusinessName}
                                        </span>
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {`${FirstName} ${Surname}`}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ParentMember',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'ORDERED_BY'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { BusinessName, FirstName, Surname } = row.original;

                            if(BusinessName) {
                                return (
                                    <React.Fragment>
                                        <span className='react-table__info'>
                                            {BusinessName}
                                        </span>
                                    </React.Fragment>
                                );
                            }

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {`${FirstName} ${Surname}`}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'BusinessName',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'DATE_ORDERED'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.original.PlacedNice}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Placed',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading hidden-xs'>
                                        <LocalizedMessage id={'ORDER_STATUS'} />
                                    </span>
                                    <span className='react-table__heading visible-xs'>
                                        <LocalizedMessage id={'STATUS'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        minWidth: undefined,
                        accessor: 'Status',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'CONTAINS'} />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'ContainItemTypes',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage
                                            id={'PAYMENT_TYPE'}
                                        />
                                    </span>
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {convertPaymentMethodToAReadableFormat(row.value)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        sortable: false,
                        minWidth: undefined,
                        accessor: 'PaymentMethod',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage id={'TOTAL'} />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            const { CurrencyDisplayCode, CurrencyFormat } = row.original;
                            const { locale } = currencyObject;

                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {currencyDisplay(row.value, CurrencyDisplayCode, CurrencyFormat, locale)}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'Total',
                    },
                    {
                        Header: () => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__heading'>
                                        <LocalizedMessage
                                            id={'CARRIER'}
                                        />
                                    </span>
                                    <span className='react-table__sort-icon' />
                                </React.Fragment>
                            );
                        },
                        Cell: (row) => {
                            return (
                                <React.Fragment>
                                    <span className='react-table__info'>
                                        {row.value}
                                    </span>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'hidden-xs',
                        className: 'hidden-xs',
                        minWidth: undefined,
                        accessor: 'ShippingMethod',
                    },
                    {
                        expander: true,
                        Expander: ({ isExpanded, ...rest }) => {
                            return (
                                <React.Fragment>
                                    <button
                                        type='button'
                                        className='react-table__extra-data-toggle'
                                    >
                                        <span className='icon icon-chevron-thick' />
                                    </button>
                                </React.Fragment>
                            );
                        },
                        headerClassName: 'visible-xs',
                        className: 'visible-xs',
                        width: 45,
                        sortable: false,
                        accessor: 'ID',
                    },
                ];

                return tableProps;
            },
        };
    };

    return (
        <React.Fragment>
            {/*
            <div className='vspace'>
                <OrderList
                    className='card--stackable'
                    heading={getTranslation(
                        'CUSTOMER_ORDERS.AWAITING_APPROVAL.HEADING'
                    )}
                    refineResultsModalKey='refine-results-customer-orders-awaiting-approval-list'
                    viewOrderModalKey='view-customer-order-awaiting-approval-modal'
                    callback={getAwaitingApprovalCustomerOrdersCallback()}
                    requestPropWatcher={awaitingApprovalCustomerOrdersWatcher}
                    showStatus='ShippingStatus'
                    sortBy='Placed'
                    sortDirection='ASC'
                />
            </div>
            */}
            <div className='vspace'>
                <OrderList
                    className='card--stackable'
                    heading={getTranslation(
                        'CUSTOMER_ORDERS.RECENT_CUSTOMER_ORDERS.HEADING'
                    )}
                    refineResultsModalKey='refine-results-customer-orders-recent-orders-list'
                    viewOrderModalKey='view-customer-order-recent-orders-modal'
                    callback={getRecentCustomerOrdersCallback()}
                    requestPropWatcher={recentCustomerOrdersWatcher}
                    showStatus='ShippingStatus'
                    sortBy='Placed'
                    sortDirection='DESC'
                />
            </div>
        </React.Fragment>
    );
}

export default CustomerOrders;
